import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import { formatNumber } from "../../plans";
import Button from "../../ui/Button";
import Reveal from "../../ui/Reveal";
import SectionTitle from "../shared/SectionTitle";
import { faceStyle, type Face } from "./faces";

// Stories are content, not chrome: they keep the brand's deep navy in light and dark, like a photo would.
const DEEP = "#0b114d";
type Story = { face: Face; img?: number; tone?: "paper" | "accent" | "deep" };
const STORIES: Story[] = [
  { img: 1, face: ["peyda", 800] },
  { tone: "paper", face: ["modam", 700] },
  { img: 2, face: ["modam", 800] },
  { img: 3, face: ["yekan", 300] },
  { tone: "accent", face: ["peyda", 900] },
  { img: 4, face: ["peyda", 500] },
  { tone: "deep", face: ["yekan", 900] },
  { img: 5, face: ["modam", 300] },
];

// A native scroll-snap strip of sample stories (photo + live Persian type). Swipe on touch; arrow buttons from md up.
export default function StoriesStrip() {
  const { t } = useTranslation();
  const texts = t("ui.landing.b.stories.items", { returnObjects: true }) as string[];
  const rail = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  // The first/last card being (almost) fully visible inside the rail disables that arrow.
  useEffect(() => {
    const el = rail.current;
    const items = el?.querySelectorAll("li");
    if (!el || !items?.length) return;
    const first = items[0], last = items[items.length - 1];
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const full = e.intersectionRatio >= 0.9;
          setEdge((s) => (e.target === first ? { ...s, start: full } : { ...s, end: full }));
        }
      },
      { root: el, threshold: [0, 0.9, 1] },
    );
    io.observe(first);
    io.observe(last);
    return () => io.disconnect();
  }, []);

  // aria-disabled (not disabled) at the ends, so a focused arrow keeps focus instead of dropping it to <body>
  const go = (dir: 1 | -1) => {
    const el = rail.current;
    if (!el || (dir === 1 ? edge.end : edge.start)) return;
    const rtl = getComputedStyle(el).direction === "rtl";
    const smooth = !matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({ left: dir * (rtl ? -1 : 1) * el.clientWidth * 0.8, behavior: smooth ? "smooth" : "auto" });
  };

  const pad = "px-[calc(max(0px,(100%_-_1200px)/2)_+_var(--gutter))] scroll-px-[calc(max(0px,(100%_-_1200px)/2)_+_var(--gutter))]";
  return (
    <section aria-labelledby="stories-title" className="pb-[clamp(64px,9vw,128px)]">
      <Reveal className="mx-auto mb-[clamp(16px,2.5vw,28px)] flex w-full max-w-[1200px] items-end justify-between gap-6 px-[var(--gutter)]">
        <SectionTitle id="stories-title" title={t("ui.landing.b.stories.title")} lead={t("ui.landing.b.stories.lead")} />
        <div className="hidden shrink-0 gap-2 md:flex">
          <Button variant="secondary" className="w-11 px-0" aria-label={t("ui.landing.b.stories.prev")} aria-disabled={edge.start} onClick={() => go(-1)}>
            <ChevronRight className="size-5 ltr:rotate-180" strokeWidth={1.75} aria-hidden />
          </Button>
          <Button variant="secondary" className="w-11 px-0" aria-label={t("ui.landing.b.stories.next")} aria-disabled={edge.end} onClick={() => go(1)}>
            <ChevronLeft className="size-5 ltr:rotate-180" strokeWidth={1.75} aria-hidden />
          </Button>
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <div
          ref={rail}
          role="region"
          aria-label={t("ui.landing.b.stories.label")}
          tabIndex={0}
          className={cn(
            "snap-x snap-mandatory overflow-x-auto overscroll-x-contain pb-7 pt-2 [scrollbar-width:none] focus-visible:![outline-offset:-2px] [&::-webkit-scrollbar]:hidden",
            pad,
          )}
        >
          <ul className="flex w-max items-start gap-4">
            {STORIES.map((s, i) => (
              <li key={i} className="shrink-0 snap-start lg:even:mt-11">
                <StoryCard story={s} text={texts[i]} />
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </section>
  );
}

function StoryCard({ story, text }: { story: Story; text: string }) {
  const { t } = useTranslation();
  const { img, tone, face } = story;
  return (
    <figure className="group m-0 w-[200px]">
      <div
        className={cn(
          "relative isolate h-[268px] overflow-hidden rounded-lg border border-line transition-transform duration-[450ms] ease-out group-hover:-translate-y-1",
          tone === "paper" ? "bg-raised text-ink" : tone === "accent" ? "border-transparent bg-accent text-on-accent" : "text-[#f7f9ff]",
        )}
        style={img || tone === "deep" ? { background: DEEP } : undefined}
      >
        {img && (
          <div className="absolute inset-x-0 top-0 h-[140px] overflow-hidden [-webkit-mask-image:linear-gradient(#000_52%,transparent)] [mask-image:linear-gradient(#000_52%,transparent)]">
            {/* top of the photo only: the bottom 40% carries a baked-in caption */}
            <img
              src={`/img/slide_${img}.png`}
              width={202}
              height={266}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute left-1/2 top-0 h-auto w-[220px] max-w-none -translate-x-1/2"
            />
          </div>
        )}
        <p
          className={cn(
            "absolute inset-x-[18px] text-balance leading-[1.35]",
            img ? "bottom-5 text-[1.6rem]" : "top-1/2 -translate-y-1/2 text-center text-[2.1rem]",
          )}
          style={faceStyle(face)}
        >
          {text}
        </p>
      </div>
      <figcaption className="mt-3 text-[13.5px] text-ink-3">
        <b className="me-1.5 font-display text-sm font-bold text-ink">{t(`ui.landing.b.fonts.${face[0]}`)}</b>
        {t("ui.landing.b.weight", { weight: formatNumber(face[1]) })}
      </figcaption>
    </figure>
  );
}
